import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { motion } from "framer-motion";
import { FiArrowLeft, FiLock, FiMail, FiLogIn } from "react-icons/fi";
import { CgSpinner } from "react-icons/cg";
import { TextField } from "@mui/material";
import { resellerLogin } from "../../services/auth.service";
import { setUser } from "../../store/reducers/userReducer";
import { getBussinessData } from "../../store/actions/action";
import { getFingerprint } from "../../helpers/fingerprint";
import { MdFingerprint } from "react-icons/md";
import { startAuthentication } from "@simplewebauthn/browser";
import { passkeyService } from "../../services/passkey.service";

const ResellerLogin = ({ onBack }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [theme] = useState(() => localStorage.getItem("theme") || "light");
  const [showeyePassword, setShoweyePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const getTextFieldSx = (theme) => ({
    "& .MuiInputLabel-root": {
      color: theme === "dark" ? "#9ca3af" : undefined,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme === "dark" ? "#374151" : "#f9fafb",
      color: theme === "dark" ? "#f3f4f6" : "#111827",
      "& fieldset": {
        borderColor: theme === "dark" ? "#4b5563" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#3b82f6",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3b82f6",
        borderWidth: "2px",
      },
    },
    "& .MuiFormHelperText-root": {
      color: theme === "dark" ? "#ef4444" : undefined,
    },
  });

  const togglePasswordVisibility = () => {
    setShoweyePassword((prev) => !prev);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = t("Email is Required");
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t("Enter a valid email");
    }

    if (!formData.password) {
      newErrors.password = t("Password is Required");
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setLoading(true);
      try {
        const fingerprint = await getFingerprint();
        const result = await resellerLogin({ ...formData, fingerprint });
        if (result?.status === 200) {
          const { user, businessId } = result.data;
          await localStorage.setItem(
            "token",
            result.headers?.get("x-auth-token"),
          );
          await localStorage.setItem("role", user?.position);
          await localStorage.setItem("userUuid", user?.userUuid);
          await localStorage.setItem("bussinessId", businessId);
          if (result.data.reseller) {
            await localStorage.setItem(
              "resellerId",
              result.data.reseller.resellerId,
            );
          }

          dispatch(setUser(user));
          dispatch(getBussinessData(businessId));

          toast.success(t("Login successful!"));
          navigate(`/${businessId}/dashboard`);
        } else {
          toast.error(
            result?.data?.message || t("Login failed. Please try again."),
          );
        }
      } catch (error) {
        toast.error(t("Invalid Email or Password"));
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePasskeySignIn = async () => {
    if (!formData.email) {
      setErrors({ email: t("PASSKEY.EMAIL_REQ_LOGIN") });
      return;
    }
    setLoading(true);
    try {
      const fingerprint = await getFingerprint();
      const {
        data: { options, challengeKey },
      } = await passkeyService.generateAuthenticationOptions({
        email: formData.email,
      });

      const response = await startAuthentication(options);

      const verificationResp = await passkeyService.verifyAuthentication({
        response,
        challengeKey,
        fingerprint,
      });

      if (verificationResp.data?.hasBusiness) {
        const { user, businessId } = verificationResp.data;
        const token =
          verificationResp.headers?.["x-auth-token"] ||
          verificationResp.headers?.get?.("x-auth-token");
        if (token) await localStorage.setItem("token", token);
        await localStorage.setItem("role", user?.position);
        await localStorage.setItem("userUuid", user?.userUuid);
        await localStorage.setItem("bussinessId", businessId);

        dispatch(setUser(user));
        dispatch(getBussinessData(businessId));

        navigate(`/${businessId}/dashboard`);
        toast.success(t("PASSKEY.LOGIN_SUCCESS"));
      } else {
        toast.error(t("PASSKEY.LOGIN_FAILED"));
      }
    } catch (err) {
      console.error(err);
      const errorMsg =
        err?.response?.data?.error ||
        err?.message ||
        t("PASSKEY.LOGIN_CANCELLED");
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='w-full'>
      <form onSubmit={handleSubmit}>
        <motion.div
          initial='hidden'
          animate='visible'
          variants={staggerChildren}
          className='space-y-6'
        >
          <motion.div variants={fadeInUp}>
            <TextField
              type='email'
              name='email'
              label={t("Email")}
              fullWidth
              value={formData.email}
              onChange={handleChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
              variant='outlined'
              size='medium'
              InputProps={{
                startAdornment: <FiMail className='mr-2 text-gray-400' />,
              }}
              sx={getTextFieldSx(theme)}
            />
          </motion.div>

          <motion.div variants={fadeInUp}>
            <TextField
              type={showeyePassword ? "text" : "password"}
              name='password'
              label={t("Password")}
              fullWidth
              value={formData.password}
              onChange={handleChange}
              error={Boolean(errors.password)}
              helperText={errors.password}
              variant='outlined'
              size='medium'
              InputProps={{
                startAdornment: <FiLock className='mr-2 text-gray-400' />,
                endAdornment: (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    type='button'
                    onClick={togglePasswordVisibility}
                    className={`p-1 ${theme === "dark" ? "text-gray-400 hover:text-blue-300" : "text-gray-400 hover:text-blue-600"}`}
                  >
                    {showeyePassword ? <FaEyeSlash /> : <FaEye />}
                  </motion.button>
                ),
              }}
              sx={getTextFieldSx(theme)}
            />
          </motion.div>

          <motion.button
            variants={fadeInUp}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type='submit'
            disabled={loading || !formData.email || !formData.password}
            className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-3 mt-4 ${
              loading || !formData.email || !formData.password
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl"
            }`}
          >
            {loading ? (
              <>
                <CgSpinner className='animate-spin text-2xl' />
                <span>{t("Logging in...")}</span>
              </>
            ) : (
              <>
                <div className='p-2 bg-white/20 rounded-lg'>
                  <FiLogIn className='text-xl' />
                </div>
                <span>{t("Login as Reseller")}</span>
              </>
            )}
          </motion.button>

          <motion.button
            variants={fadeInUp}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type='button'
            onClick={handlePasskeySignIn}
            disabled={loading || !formData.email}
            className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-3 mt-2 ${
              loading || !formData.email
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                : theme === "dark"
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200"
            }`}
          >
            <MdFingerprint className='text-2xl' />
            <span>{t("PASSKEY.SIGN_IN")}</span>
          </motion.button>

          <motion.button
            variants={fadeInUp}
            type='button'
            onClick={onBack}
            whileHover={{ x: -2 }}
            className={`w-full mt-4 py-2 font-semibold text-sm transition-all flex items-center justify-center gap-2 group ${
              theme === "dark"
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <FiArrowLeft className='group-hover:translate-x-[-2px] transition-transform' />
            <span>{t("Back to Login")}</span>
          </motion.button>
        </motion.div>
      </form>
    </div>
  );
};

export default ResellerLogin;
